import { useCallback } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { createMutationAtom } from 'atoms/create-resource-atom';
import {
  toastAtom,
  ltdRefreshTriggerAtom,
  updateGeometriesRefreshTriggerAtom,
  slotValuationRefreshTriggerAtom,
} from 'pages/type-curves/atoms';
import type {
  InventorySlotRequestResponse,
  InventoryFormationSlotsBulkResponse,
  InventoryFormationSlotEdit,
} from 'types/api-responses';
import type { SlotGeometryEdit } from 'types/api-payloads';
import type { BuilderSpacing } from './use-builder-spacing';
import { useRelandWatch } from './hooks';
import { relandToast } from './utils';

const generateAtom = createMutationAtom<
  InventorySlotRequestResponse,
  'createInventorySlotRequest'
>();
const slotEditsAtom = createMutationAtom<
  InventoryFormationSlotsBulkResponse,
  'inventoryWorkspaceFormationSlotsBulk'
>();
const slotEditAtom = createMutationAtom<
  InventoryFormationSlotEdit,
  'inventoryWorkspaceFormationSlot'
>();

interface IneligibleSlot {
  slotId?: number;
  reason?: string;
}

interface ErrorBody {
  errors?: {
    code?: string;
    message?: string;
    details?: { ineligible?: IneligibleSlot[] };
  }[];
  error?: string;
}

const firstMessage = (err?: ErrorBody, fallback = 'Request failed') =>
  err?.errors?.[0]?.message || err?.error || fallback;

// A SELECTION_NOT_EDITABLE 422 lists every offending slot + reason — surface them
// so the operator knows which to fix/remove (the batch is atomic; nothing wrote).
const ineligibleSubtitle = (err?: ErrorBody): string | undefined => {
  const ineligible = err?.errors?.[0]?.details?.ineligible;
  if (!ineligible?.length) return undefined;
  return ineligible
    .map((slot) => `slot ${slot.slotId ?? '?'}: ${slot.reason ?? 'ineligible'}`)
    .join('; ');
};

export interface BuilderGeneration {
  /** Re-lay the whole unit at the current spacing. */
  generateInventory: (args: {
    unitId: number;
    inventoryFormationPackageId: number | null;
    spacing: BuilderSpacing;
    ignoreExistingDevelopment: boolean;
  }) => void;
  generating: boolean;
  /** Persist the pending manual gun-barrel edits (move/trim) to the slots. */
  updateGeometries: (args: {
    unitId: number;
    edits: SlotGeometryEdit[];
  }) => void;
  updating: boolean;
  /**
   * Persist one slot's valued flag and note. Unvaluing requires a note;
   * re-valuing does not, and leaves the existing note in place.
   */
  updateValuation: (args: {
    slotId: number;
    valued: boolean;
    notes: string | null;
    onSaved?: () => void;
    onFailed?: () => void;
  }) => void;
  valuing: boolean;
  /**
   * Follow landing edits already in flight — the ones a freshly drawn chart
   * still finds pending — so their resolution refreshes the page the way a
   * batch started here does.
   */
  watchLandings: (editIds: number[]) => void;
}

/**
 * The builder's two commit paths.
 *
 * Generate LTD Inventory re-lays the whole unit (synchronous single-unit route:
 * the 200 IS the finished run). Update Geometries persists the operator's manual
 * gun-barrel edits (X/Y moves + lateral trims) via the synchronous Slot Editing
 * PATCH — it returns the updated slots directly (no Pusher). Generate LTD bumps
 * `ltdRefreshTriggerAtom`; Update Geometries bumps
 * `updateGeometriesRefreshTriggerAtom`. Both feed `geometryRefreshAtom`, so each
 * refetches the same set on BOTH surfaces: gun barrel, slot table, spacing
 * configs, map slots, dev-area MVT, and the inventory-package api (the zone's
 * inventory details are re-resolved either way).
 *
 * @param {number} inventoryWorkspaceId - The workspace both calls are scoped to.
 * @param {Function} onCompleted - Refetch/clear hook, called once a run finishes.
 * @return {BuilderGeneration} The two actions and their in-flight state.
 */
export function useBuilderGeneration(
  inventoryWorkspaceId: number,
  onCompleted?: () => void
): BuilderGeneration {
  const setToast = useSetAtom(toastAtom);
  // Generate LTD re-lays the unit → bump the LTD trigger.
  const bumpLtdRefresh = useSetAtom(ltdRefreshTriggerAtom);
  // Update Geometries persists a slot move/trim → bump its own trigger. Both
  // triggers feed `geometryRefreshAtom` and so drive the same fan-out; the split
  // only names which action fired.
  const bumpUpdateGeometriesRefresh = useSetAtom(
    updateGeometriesRefreshTriggerAtom
  );
  // valued/notes moves nothing, so it drives its own narrow trigger rather than
  // the geometry fan-out.
  const bumpValuationRefresh = useSetAtom(slotValuationRefreshTriggerAtom);
  const [{ loading: generateLoading }, runGenerate] = useAtom(generateAtom);
  const [{ loading: slotEditsLoading }, runSlotEdits] = useAtom(slotEditsAtom);
  const [{ loading: slotEditLoading }, runSlotEdit] = useAtom(slotEditAtom);

  // A permit moved in depth is re-landed on the request; its landing analysis and any formation
  // change resolve afterwards on the workspace channel (Story 1). Each one that reports refreshes
  // the same surfaces Update Geometries does and says where the well landed.
  const watchRelands = useRelandWatch(
    inventoryWorkspaceId,
    (evt) => {
      bumpUpdateGeometriesRefresh((prev) => prev + 1);
      onCompleted?.();
      setToast(relandToast(evt));
    },
    // Reconcile once the subscription is live: a landing that reported before
    // it was is already in the server state this refetch reads.
    () => bumpUpdateGeometriesRefresh((prev) => prev + 1)
  );

  const generateInventory = useCallback(
    ({
      unitId,
      inventoryFormationPackageId,
      spacing,
      ignoreExistingDevelopment,
    }: {
      unitId: number;
      inventoryFormationPackageId: number | null;
      spacing: BuilderSpacing;
      ignoreExistingDevelopment: boolean;
    }) => {
      // Refused rather than sent as 0. The server cannot resolve zone 0, and
      // reports the *other* accepted parameter pair as missing — an error that
      // reads as an unrelated fault and cost real time to trace.
      if (inventoryFormationPackageId === null) {
        setToast({
          kind: 'error',
          title: 'This formation has no inventory package yet',
          subtitle: 'Promote the workspace before generating LTD inventory.',
        });
        return;
      }

      runGenerate({
        url: `/inventory_workspaces/${inventoryWorkspaceId}/units/${unitId}/slot_inventory_requests`,
        type: 'POST',
        data: {
          generation: {
            // The single-unit route scopes the run; the zone id resolves which
            // (type curve x package) it belongs to. Non-null by the guard above.
            inventoryFormationPackageId,
            force: true,
            wellSpacingFt: spacing.wellSpacingFt,
            crossLineSetbackFt: spacing.crossLineSetbackFt,
            inLineSetbackFt: spacing.inLineSetbackFt,
            spacingToleranceFt: spacing.spacingToleranceFt,
            // Sent every run — an absent/false value resets a prior `true`.
            ignoreExistingDevelopment,
          },
        },
        // Synchronous route: the 200 IS the finished run. No Pusher, no poll.
        onSuccess: (data) => {
          if (data?.status === 'empty') {
            setToast({
              kind: 'success',
              title: 'Nothing to generate',
              subtitle:
                data.message ?? 'No units were eligible for generation.',
            });
            return;
          }
          if (data?.status === 'failed') {
            setToast({
              kind: 'error',
              title: 'LTD generation failed',
              subtitle:
                data.failureReason ??
                data.units?.[0]?.error ??
                'The unit failed to generate.',
            });
            return;
          }
          // status === 'processed' — redraw the new slots and report counts.
          bumpLtdRefresh((prev) => prev + 1);
          onCompleted?.();
          const created = data?.summary?.ltdSlotsCreated ?? 0;
          setToast({
            kind: 'success',
            title: 'LTD inventory generated',
            subtitle: `${created} LTD slot${created === 1 ? '' : 's'} created.`,
          });
        },
        onError: (err?: ErrorBody) =>
          setToast({
            kind: 'error',
            title: 'Unable to generate inventory',
            subtitle: firstMessage(err, 'Generation could not be started.'),
          }),
      });
    },
    [inventoryWorkspaceId, runGenerate, setToast, bumpLtdRefresh, onCompleted]
  );

  const updateGeometries = useCallback(
    ({ edits }: { unitId: number; edits: SlotGeometryEdit[] }) => {
      if (edits.length === 0) return;
      runSlotEdits({
        url: `/inventory_workspaces/${inventoryWorkspaceId}/formation_slots`,
        type: 'PATCH',
        data: { edits },
        // Synchronous: the 200 returns the persisted slots. Bump the Update
        // Geometries trigger, which (via geometryRefreshAtom) refetches the gun
        // barrel / slot table / spacing / map slots / dev-area MVT and the
        // inventory-package api, on both surfaces.
        onSuccess: (data) => {
          bumpUpdateGeometriesRefresh((prev) => prev + 1);
          onCompleted?.();
          const landing = data?.landingEdits ?? [];
          setToast({
            kind: 'success',
            title: 'Geometries updated',
            subtitle:
              landing.length > 0
                ? `${landing.length} ${
                    landing.length === 1 ? 'well is' : 'wells are'
                  } being re-landed; the chart refreshes when the landing analysis finishes.`
                : undefined,
          });
          watchRelands(landing.map((edit) => edit.id));
        },
        // Atomic on failure — leave the pending edits intact (don't clear).
        onError: (err?: ErrorBody) =>
          setToast({
            kind: 'error',
            title: 'Unable to update geometries',
            subtitle:
              ineligibleSubtitle(err) ??
              firstMessage(err, 'The slots could not be updated.'),
          }),
      });
    },
    [
      inventoryWorkspaceId,
      runSlotEdits,
      setToast,
      bumpUpdateGeometriesRefresh,
      onCompleted,
      watchRelands,
    ]
  );

  // The member PATCH, not the collection one Update Geometries uses: this edits
  // exactly one slot, so its id belongs in the URL. valued/notes are ops like a
  // move is, and apply to any slot type, not just LTDs.
  const updateValuation = useCallback(
    ({
      slotId,
      valued,
      notes,
      onSaved,
      onFailed,
    }: {
      slotId: number;
      valued: boolean;
      notes: string | null;
      onSaved?: () => void;
      onFailed?: () => void;
    }) => {
      // An absent/empty note is not sent: the server treats blank as "leave
      // alone", which is what re-valuing wants.
      const edit: Omit<SlotGeometryEdit, 'slotId'> = { valued };
      if (notes) edit.notes = notes;

      runSlotEdit({
        url: `/inventory_workspaces/${inventoryWorkspaceId}/formation_slots/${slotId}`,
        type: 'PATCH',
        data: { edit },
        // The narrow trigger, not the geometry fan-out: valued/notes moves
        // nothing, so the spacing configs, map slot layer, dev-area tiles and
        // package api have nothing to re-read. The gun barrel and slot table do
        // — they carry the flag, and refreshing them keeps `serverSlots`
        // current so a second right-click toggles from the saved value.
        onSuccess: () => {
          bumpValuationRefresh((prev) => prev + 1);
          onSaved?.();
          setToast({
            kind: 'success',
            title: valued ? 'Property valued' : 'Property unvalued',
          });
        },
        onError: (err?: ErrorBody) => {
          onFailed?.();
          setToast({
            kind: 'error',
            title: 'Unable to update the property',
            subtitle:
              ineligibleSubtitle(err) ??
              firstMessage(err, 'The note could not be saved.'),
          });
        },
      });
    },
    [inventoryWorkspaceId, runSlotEdit, setToast, bumpValuationRefresh]
  );

  return {
    generateInventory,
    generating: generateLoading,
    updateGeometries,
    updating: slotEditsLoading,
    updateValuation,
    valuing: slotEditLoading,
    watchLandings: watchRelands,
  };
}
